import { useRef, useState, type CSSProperties } from "react";
import { Link, useLocation, useParams } from "react-router";
import { pollRecording } from "@/api/client";
import type { Recording, RecordingFrame, RecordingShark } from "@/api/types";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { usePolling } from "@/hooks/usePolling";
import { formatClock, formatPercent, pluralize, viewLabel } from "@/lib/format";
import { formatStamp, STATUS } from "@/site/identify";
import { Failure, Loading, RarityMark, SectionHead, to } from "@/site/parts";
import { SpotGlyph } from "@/site/plate";
import { frameAt, headline, summary, tally } from "@/site/recording-state";
import type { RecordingNavState } from "@/site/upload";

/** The sampling interval as printed: whole seconds plainly, a long clip's spread to 0.1 s. */
const intervalText = (s: number) => (Number.isInteger(s) ? String(s) : s.toFixed(1));

function RecordingBar({ id, recording }: { id: string; recording: Recording | null }) {
  const processing = !recording || recording.status === "processing";
  const word = { processing: "Processing", done: "Measured", error: "Fault" }[
    recording?.status ?? "processing"
  ];
  return (
    <div className="statusbar label" aria-live="polite">
      <span>
        Recording{" "}
        <span style={{ color: "var(--ink)", textTransform: "none", letterSpacing: 0 }}>{id}</span>
      </span>
      <span>
        Status{" "}
        <span style={{ color: "var(--ink)" }}>
          <span
            className={`status-dot${processing ? " blink" : ""}`}
            data-tone={recording?.status === "error" ? "off" : "signal"}
          />
          {word}
        </span>
      </span>
      {recording && <span>Received {formatStamp(recording.created_at)}</span>}
      <Link to={to.home} className="link-u" style={{ marginLeft: "auto" }}>
        New measurement →
      </Link>
    </div>
  );
}

function RecordingHead({ recording }: { recording: Recording }) {
  const t = tally(recording);
  const rows: [string, string][] = [
    ["Length", recording.duration === null ? "—" : formatClock(recording.duration)],
    ["Sampling", `1 frame / ${intervalText(recording.interval)} s`],
    ["Frames measured", `${t.measured} / ${t.total}`],
    ["Matched", String(t.match)],
    ["Inconclusive", String(t.uncertain)],
    ["Not on record", String(t.new)],
    ["No signal", String(t.noSignal)],
  ];
  return (
    <section className="in-wrap in-grid" style={{ paddingTop: 48, rowGap: 32 }}>
      <div style={{ gridColumn: "span 8" }} className="anim-in">
        <p className="label label-signal">
          {recording.status === "processing" ? "Result · pending" : "Result · recording"}
        </p>
        <h1 className="display-2" style={{ marginTop: 14 }}>
          {headline(recording)}
        </h1>
        <p className="muted" style={{ marginTop: 18, maxWidth: "38em" }}>
          {summary(recording)} Each frame is identified like a photograph; open one for its
          evidence, or to log the sighting.
        </p>
        {recording.message && (
          <p role="alert" className="alert" style={{ marginTop: 18 }}>
            {recording.message}
          </p>
        )}
      </div>
      <div style={{ gridColumn: "9 / span 4" }} className="anim-in">
        <span className="conf-bar recording-progress" aria-hidden="true">
          <span style={{ width: `${t.total ? (100 * t.measured) / t.total : 0}%` }} />
        </span>
        <dl className="kv" style={{ gridTemplateColumns: "1fr auto", marginTop: 12 }}>
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd className="mono" style={{ textAlign: "right", fontSize: 13 }}>
                {v}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/** Where each sampled frame sits in the clip, coloured by its outcome. */
function Timeline({
  recording,
  current,
  onSeek,
}: {
  recording: Recording;
  current: number;
  onSeek: ((seconds: number) => void) | null;
}) {
  const end = recording.duration ?? (recording.frames.at(-1)?.time ?? 0) + recording.interval;
  return (
    <ol className="timeline" aria-label="Sampled frames along the video">
      {recording.frames.map((f, i) => {
        const style = { left: `${(100 * f.time) / Math.max(end, 1e-6)}%` } as CSSProperties;
        const label = `${formatClock(f.time)} · ${STATUS[f.status].word}`;
        return (
          <li key={f.query_id} className="timeline-mark" style={style} data-status={f.status}>
            {onSeek ? (
              <button
                type="button"
                aria-label={`Play from ${label}`}
                data-active={i === current}
                onClick={() => onSeek(f.time)}
              />
            ) : (
              <Link to={to.identify(f.query_id)} aria-label={label} data-active={i === current} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The uploaded video, played back while this browser session still holds the file. */
function SessionVideo({
  src,
  recording,
  current,
  onTime,
  onLost,
}: {
  src: string;
  recording: Recording;
  current: number;
  onTime: (seconds: number) => void;
  onLost: () => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const seek = (seconds: number) => {
    if (ref.current) ref.current.currentTime = seconds;
    onTime(seconds);
  };
  return (
    <>
      <video
        ref={ref}
        src={src}
        className="recording-video"
        controls
        muted
        playsInline
        onTimeUpdate={(e) => onTime(e.currentTarget.currentTime)}
        onError={onLost}
      />
      <Timeline recording={recording} current={current} onSeek={seek} />
    </>
  );
}

function sharkLine(s: RecordingShark): string {
  const also = s.frames > s.matched ? ` (+${s.frames - s.matched} inconclusive)` : "";
  const best = `best ${formatPercent(s.best.confidence)} at ${formatClock(s.best.time)}`;
  return `Matched in ${pluralize(s.matched, "frame")}${also} · ${best}`;
}

function PossibleTable({ sharks }: { sharks: RecordingShark[] }) {
  return (
    <div className="scroll-x">
      <table className="table">
        <thead>
          <tr>
            <th>Specimen</th>
            <th className="r">Frames</th>
            <th className="r">Best P(same)</th>
            <th className="r">At</th>
            <th className="r">Evidence</th>
          </tr>
        </thead>
        <tbody>
          {sharks.map((s) => (
            <tr key={s.shark.id}>
              <td>
                <Link to={to.shark(s.shark.id)} className="link-u">
                  {s.shark.display_name}
                </Link>
                <span className="label" style={{ display: "block" }}>
                  {s.shark.id}
                </span>
              </td>
              <td className="r mono">{s.frames}</td>
              <td className="r mono">{formatPercent(s.best.confidence)}</td>
              <td className="r mono">{formatClock(s.best.time)}</td>
              <td className="r">
                <Link to={to.identify(s.best.query_id)} className="link-u">
                  Frame →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SharkCard({
  seen,
  frame,
  index,
}: {
  seen: RecordingShark;
  frame?: RecordingFrame;
  index: number;
}) {
  const shark = seen.shark;
  return (
    <article className="spec anim-in" style={{ "--i": index } as CSSProperties}>
      <div className="mono label" style={{ display: "flex", justifyContent: "space-between" }}>
        <Link to={to.shark(shark.id)} className="link-u" style={{ color: "var(--ink)" }}>
          {shark.id}
        </Link>
        <span className="label-signal">Matched</span>
      </div>
      <div className="spec-img">
        {frame ? (
          <img src={frame.image_url} alt={`${shark.display_name} at ${formatClock(frame.time)}`} />
        ) : shark.constellation ? (
          <SpotGlyph constellation={shark.constellation} label={shark.display_name} />
        ) : null}
      </div>
      <p className="spec-name">
        <Link to={to.shark(shark.id)}>{shark.display_name}</Link>
      </p>
      <div style={{ display: "grid", gap: 6 }}>
        <RarityMark rarity={shark.rarity} />
        <span className="label">{sharkLine(seen)}</span>
      </div>
      <Link
        to={to.identify(seen.best.query_id)}
        className="btn btn-ghost"
        style={{ marginTop: "auto" }}
      >
        Evidence · log sighting <span className="btn-arrow">→</span>
      </Link>
    </article>
  );
}

function frameCaption(f: RecordingFrame): string {
  if (f.top) return `${f.top.display_name} · ${formatPercent(f.top.confidence)}`;
  if (f.status === "new") return "Not on record · can be named";
  if (f.status === "processing") return "Reading the spots…";
  if (f.status === "no_shark" && f.n_spots !== null) return `${f.n_spots} spots · too few`;
  return f.status === "error" ? "Fault" : "No shark in frame";
}

function FrameCard({
  frame,
  active,
  index,
}: {
  frame: RecordingFrame;
  active: boolean;
  index: number;
}) {
  const status = STATUS[frame.status];
  const view = viewLabel(frame.view);
  return (
    <Link
      to={to.identify(frame.query_id)}
      className="spec anim-in"
      data-active={active}
      style={{ "--i": Math.min(index, 12) } as CSSProperties}
    >
      <div className="mono label" style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "var(--ink)" }}>{formatClock(frame.time)}</span>
        <span>
          <span
            className={`status-dot${frame.status === "processing" ? " blink" : ""}`}
            data-tone={status.tone}
          />
          {status.word}
        </span>
      </div>
      <div className="spec-img">
        <img src={frame.image_url} alt={`Frame at ${formatClock(frame.time)}`} loading="lazy" />
      </div>
      <span className="label" style={{ color: frame.top ? "var(--ink)" : undefined }}>
        {frameCaption(frame)}
      </span>
      {view && <span className="label">{view}</span>}
    </Link>
  );
}

export function RecordingPage() {
  const { recordingId = "" } = useParams();
  const location = useLocation();
  const video = (location.state as RecordingNavState | null)?.video;
  const { data: recording, error, retry } = usePolling(recordingId, pollRecording);
  const [time, setTime] = useState(0);
  const [lost, setLost] = useState(false);
  useDocumentMeta({
    title: recording && recording.status !== "processing" ? headline(recording) : "Measuring",
  });

  if (error && (!recording || recording.status === "processing")) {
    return (
      <>
        <div className="in-wrap">
          <RecordingBar id={recordingId} recording={recording} />
        </div>
        <Failure error={error} title="The instrument lost this recording." onRetry={retry} />
      </>
    );
  }
  if (!recording) {
    return (
      <>
        <div className="in-wrap">
          <RecordingBar id={recordingId} recording={null} />
        </div>
        <Loading label="Locating recording" />
      </>
    );
  }
  const playing = video !== undefined && !lost;
  const current = playing ? frameAt(recording.frames, time) : -1;
  const frameOf = (queryId: string) => recording.frames.find((f) => f.query_id === queryId);
  const identified = recording.sharks.filter((s) => s.matched > 0);
  // A candidate that recurs across frames is the stronger lead, so recurrence ranks first.
  const possible = recording.sharks
    .filter((s) => s.matched === 0)
    .toSorted((a, b) => b.frames - a.frames || b.best.confidence - a.best.confidence);
  const sections = [
    identified.length > 0 && "sharks",
    possible.length > 0 && "possible",
    "frames",
  ].filter(Boolean);
  const sectionIndex = (name: string) => String(sections.indexOf(name) + 1).padStart(2, "0");
  return (
    <>
      <div className="in-wrap">
        <RecordingBar id={recordingId} recording={recording} />
      </div>
      <RecordingHead recording={recording} />
      <section className="in-wrap in-section" aria-label="The recording">
        {playing ? (
          <SessionVideo
            src={video}
            recording={recording}
            current={current}
            onTime={setTime}
            // The object URL dies with the tab that made it; a reload keeps only the frames.
            onLost={() => setLost(true)}
          />
        ) : (
          <Timeline recording={recording} current={current} onSeek={null} />
        )}
      </section>
      {identified.length > 0 && (
        <section className="in-wrap in-section" aria-labelledby="recording-sharks">
          <SectionHead index={sectionIndex("sharks")} title="Matched" id="recording-sharks">
            <p className="muted">
              Each shark matched in at least one frame. Frames are judged one by one: check the
              evidence before you log the sighting from its most confident frame.
            </p>
          </SectionHead>
          <div className="spec-grid">
            {identified.map((s, i) => (
              <SharkCard key={s.shark.id} seen={s} frame={frameOf(s.best.query_id)} index={i} />
            ))}
          </div>
        </section>
      )}
      {possible.length > 0 && (
        <section className="in-wrap in-section" aria-labelledby="recording-possible">
          <SectionHead index={sectionIndex("possible")} title="Possible" id="recording-possible">
            <p className="muted">
              The best candidate of an inconclusive frame. A shark not on record tops a different
              candidate in each frame; one that recurs across frames is worth a look.
            </p>
          </SectionHead>
          <PossibleTable sharks={possible} />
        </section>
      )}
      <section className="in-wrap in-section" aria-labelledby="recording-frames">
        <SectionHead index={sectionIndex("frames")} title="Frames" id="recording-frames">
          <p className="muted">
            One frame every {intervalText(recording.interval)} seconds, each read for spots and
            matched against the register.
          </p>
        </SectionHead>
        {recording.frames.length > 0 ? (
          <div className="spec-grid">
            {recording.frames.map((f, i) => (
              <FrameCard key={f.query_id} frame={f} active={i === current} index={i} />
            ))}
          </div>
        ) : (
          recording.status === "processing" && <Loading label="Sampling the video" />
        )}
      </section>
    </>
  );
}
